import React, { useEffect, useRef, useState } from "react";
import { imageUrl } from "../lib/api";
import { Volume2, VolumeX, Play } from "lucide-react";

/**
 * Vertical "story" video player for the car detail page.
 * Autoplay muted (so mobile browsers allow it), loops, tap to toggle sound.
 */
export const StoryVideo = ({ videoPath, contentType, poster }) => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [needsTap, setNeedsTap] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const p = v.play();
    if (p && typeof p.catch === "function") {
      p.catch(() => setNeedsTap(true));
    }
  }, [videoPath]);

  if (!videoPath) return null;

  const src = imageUrl(videoPath);

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const forcePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.play().then(() => setNeedsTap(false)).catch(() => {});
  };

  return (
    <div
      className="relative mx-auto aspect-[9/16] w-full max-w-xs bg-midnight overflow-hidden border-2 border-gold/40 shadow-[0_20px_60px_-15px_rgba(212,175,55,0.5)] rounded-md"
      data-testid="story-video"
    >
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        autoPlay
        muted={muted}
        loop
        playsInline
        preload="metadata"
        className="absolute inset-0 w-full h-full object-cover"
        onClick={toggleSound}
      >
        {contentType && <source src={src} type={contentType} />}
      </video>
      {needsTap && (
        <button
          onClick={forcePlay}
          className="absolute inset-0 flex items-center justify-center bg-midnight/60 backdrop-blur-sm"
          data-testid="story-video-play"
          aria-label="Avvia video"
        >
          <div className="w-14 h-14 rounded-full bg-gold flex items-center justify-center">
            <Play size={22} className="text-midnight ml-1" fill="currentColor" />
          </div>
        </button>
      )}
      <button
        onClick={toggleSound}
        className="absolute bottom-3 right-3 h-9 w-9 flex items-center justify-center bg-black/50 hover:bg-black/80 backdrop-blur-md text-white rounded-full border border-white/20 transition-colors"
        aria-label={muted ? "Attiva audio" : "Disattiva audio"}
        data-testid="story-video-sound-toggle"
      >
        {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
      </button>
      <div className="absolute top-3 left-3 text-[9px] uppercase tracking-widest font-bold text-gold bg-midnight/70 backdrop-blur-md px-2 py-1 rounded-sm border border-gold/40">
        Story
      </div>
    </div>
  );
};

export default StoryVideo;
